import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Plus, Save, RotateCcw, Trash2, Lock, Users, History } from 'lucide-react'
import { rolesApi, adminUsersApi } from '../../lib/api'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { useAuth } from '../../hooks/useAuth'
import LoadingState from '../../components/shared/LoadingState'
import PermissionMatrix from '../../components/shared/PermissionMatrix'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import type { AdminRoleInfo } from '../../types'
import { tx } from '../../lib/i18n'

const errorOf = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

const sameSet = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((x) => b.has(x))

/** Roles & permissions: create roles, tick what each role may do (menu × action). */
const PermissionsPage: React.FC = () => {
  const qc = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()
  const { currentAdmin } = useAuth()

  const { data: catalog, isLoading: loadingCatalog } = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: () => rolesApi.catalog().then((r) => r.data.data),
    staleTime: Infinity,
  })
  const { data: roles = [], isLoading: loadingRoles } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => rolesApi.list().then((r) => r.data.data ?? []),
  })
  const { data: admins = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminUsersApi.list().then((r) => r.data.data ?? []),
    enabled: can('admin-users.view'),
  })

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected: AdminRoleInfo | undefined = roles.find((r) => r.id === selectedId) ?? roles[0]
  const [draft, setDraft] = useState<Set<string>>(new Set())
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [newRole, setNewRole] = useState({ label: '', name: '', description: '', copyFrom: '' })

  // load the selected role into the editor
  useEffect(() => {
    if (!selected) return
    setDraft(new Set(selected.permissions))
    setLabel(selected.label)
    setDescription(selected.description ?? '')
  }, [selected?.id, selected?.permissions.join(','), selected?.label, selected?.description]) // eslint-disable-line react-hooks/exhaustive-deps

  const original = useMemo(() => new Set(selected?.permissions ?? []), [selected])
  const dirty = !!selected && (!sameSet(draft, original) || label !== selected.label || description !== (selected.description ?? ''))
  const added = [...draft].filter((p) => !original.has(p))
  const removed = [...original].filter((p) => !draft.has(p))
  const ownRole = selected && currentAdmin?.role === selected.name && currentAdmin.role !== 'super_admin'
  const readOnly = !selected || selected.locked || !!ownRole || !can('admin-users.permissions')
  const members = admins.filter((a) => selected && a.role === selected.name)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-roles'] })
    qc.invalidateQueries({ queryKey: ['admin-users'] })
  }

  const saveMut = useMutation({
    mutationFn: () =>
      rolesApi.update(selected!.id, {
        label: label.trim(),
        description: description.trim() || null,
        permissions: [...draft],
      }),
    onSuccess: () => {
      refresh()
      showToast({ type: 'success', message: tx('Role saved.') })
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to save role.')) }),
  })
  const resetMut = useMutation({
    mutationFn: () => rolesApi.update(selected!.id, { resetToDefault: true }),
    onSuccess: () => {
      refresh()
      showToast({ type: 'success', message: tx('Role reset to default permissions.') })
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to save role.')) }),
  })
  const createMut = useMutation({
    mutationFn: () =>
      rolesApi.create({
        label: newRole.label.trim(),
        name: newRole.name.trim(),
        description: newRole.description.trim() || null,
        copyFrom: newRole.copyFrom || undefined,
        permissions: [],
      }),
    onSuccess: (res) => {
      refresh()
      setCreateOpen(false)
      setNewRole({ label: '', name: '', description: '', copyFrom: '' })
      if (res.data.data) setSelectedId(res.data.data.id)
      showToast({ type: 'success', message: tx('Role created.') })
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to create role.')) }),
  })
  const deleteMut = useMutation({
    mutationFn: () => rolesApi.remove(selected!.id),
    onSuccess: () => {
      setDeleteOpen(false)
      setSelectedId(null)
      refresh()
      showToast({ type: 'success', message: tx('Role deleted.') })
    },
    onError: (e) => {
      setDeleteOpen(false)
      showToast({ type: 'error', message: errorOf(e, tx('Failed to delete role.')) })
    },
  })

  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40)
  const nameValid = /^[a-z][a-z0-9_]{1,39}$/.test(newRole.name)

  if (loadingCatalog || loadingRoles) return <LoadingState />

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-indigo-600" /> {tx('Roles & Permissions')}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {tx('Choose what each role can see and do on every menu. Per-person adjustments are on the Admin Users page.')}
          </p>
        </div>
        <div className="flex gap-2">
          {can('audit-logs.view') && (
            <Link to="/audit-logs?action=admin_role." className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="perm-history">
              <History className="h-4 w-4 text-gray-500" /> {tx('Change history')}
            </Link>
          )}
          {can('admin-users.permissions') && (
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
              data-testid="role-create-open"
            >
              <Plus className="h-4 w-4" /> {tx('Create role')}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Role list */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-2 h-fit" data-testid="role-list">
          {roles.map((r) => (
            <button
              key={r.id}
              onClick={() => {
                if (dirty && !window.confirm(tx('Discard unsaved changes?'))) return
                setSelectedId(r.id)
              }}
              className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                selected?.id === r.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50 border border-transparent'
              }`}
              data-testid={`role-item-${r.name}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm text-gray-900 truncate">{r.label}</span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <Users className="h-3.5 w-3.5" /> {r.userCount}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[11px] font-mono text-gray-400">{r.name}</span>
                {r.locked && <span className="text-[10px] px-1.5 rounded bg-rose-100 text-rose-700">{tx('All permissions')}</span>}
                {!r.locked && r.isSystem && <span className="text-[10px] px-1.5 rounded bg-gray-100 text-gray-600">{tx('Built-in')}</span>}
                {!r.locked && !r.isSystem && <span className="text-[10px] px-1.5 rounded bg-amber-100 text-amber-700">{tx('Custom')}</span>}
                {!r.locked && <span className="text-[10px] text-gray-400">{r.permissions.length} {tx('permissions')}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        {selected && catalog && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="role-editor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Role name')}</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={readOnly}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                  data-testid="role-label"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Description')}</label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={readOnly}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                  data-testid="role-description"
                />
              </div>
            </div>

            {selected.locked && (
              <div className="flex items-center gap-2 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2">
                <Lock className="h-4 w-4" /> {tx('Super Admin always has every permission and cannot be changed.')}
              </div>
            )}
            {ownRole && (
              <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
                <Lock className="h-4 w-4" /> {tx('This is your own role — only a Super Admin can change it.')}
              </div>
            )}
            {!selected.locked && selected.usesDefault && (
              <p className="text-xs text-gray-500">{tx('Using the built-in default permissions.')}</p>
            )}

            <PermissionMatrix resources={catalog.resources} value={draft} onChange={setDraft} readOnly={readOnly} testIdPrefix="role" />

            {members.length > 0 && (
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">{tx('Admins with this role')}:</span>{' '}
                {members.map((m) => `${m.firstName} ${m.lastName}`.trim() || m.email).join(', ')}
              </div>
            )}

            {!readOnly && (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500" data-testid="role-diff">
                  {dirty ? (
                    <>
                      {added.length > 0 && <span className="text-green-700">+{added.length} {tx('added')} </span>}
                      {removed.length > 0 && <span className="text-red-700">−{removed.length} {tx('removed')} </span>}
                      {selected.userCount > 0 && <span>· {tx('affects')} {selected.userCount} {tx('admins')}</span>}
                    </>
                  ) : (
                    tx('No changes')
                  )}
                </div>
                <div className="flex gap-2">
                  {!selected.isSystem && (
                    <button
                      onClick={() => setDeleteOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                      data-testid="role-delete"
                    >
                      <Trash2 className="h-4 w-4" /> {tx('Delete role')}
                    </button>
                  )}
                  {selected.isSystem && !selected.usesDefault && (
                    <button
                      onClick={() => resetMut.mutate()}
                      disabled={resetMut.isPending}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      data-testid="role-reset-default"
                    >
                      <RotateCcw className="h-4 w-4" /> {tx('Reset to default')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setDraft(new Set(selected.permissions))
                      setLabel(selected.label)
                      setDescription(selected.description ?? '')
                    }}
                    disabled={!dirty}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    data-testid="role-discard"
                  >
                    {tx('Discard changes')}
                  </button>
                  <button
                    onClick={() => saveMut.mutate()}
                    disabled={!dirty || !label.trim() || saveMut.isPending}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    data-testid="role-save"
                  >
                    <Save className="h-4 w-4" /> {saveMut.isPending ? tx('Saving...') : tx('Save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create role */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="role-create-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCreateOpen(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-900">{tx('Create role')}</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {tx('Role name')} <span className="text-red-500">*</span>
              </label>
              <input
                value={newRole.label}
                onChange={(e) =>
                  setNewRole((f) => ({ ...f, label: e.target.value, name: f.name && f.name !== slug(f.label) ? f.name : slug(e.target.value) }))
                }
                placeholder={tx('e.g. Accounting team')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="role-new-label"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {tx('Role code (a-z, 0-9, _)')} <span className="text-red-500">*</span>
              </label>
              <input
                value={newRole.name}
                onChange={(e) => setNewRole((f) => ({ ...f, name: e.target.value.toLowerCase() }))}
                placeholder="accounting_team"
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${newRole.name && !nameValid ? 'border-red-400' : 'border-gray-200'}`}
                data-testid="role-new-name"
              />
              {newRole.name && !nameValid && <p className="text-xs text-red-500 mt-1">{tx('Start with a letter; use a-z, 0-9 and _ only (2–40).')}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Description')}</label>
              <input
                value={newRole.description}
                onChange={(e) => setNewRole((f) => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="role-new-description"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Start from')}</label>
              <select
                value={newRole.copyFrom}
                onChange={(e) => setNewRole((f) => ({ ...f, copyFrom: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="role-new-copy"
              >
                <option value="">{tx('No permissions (empty)')}</option>
                {roles
                  .filter((r) => !r.locked)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {tx('Copy from')} {r.label}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                {tx('Cancel')}
              </button>
              <button
                onClick={() => createMut.mutate()}
                disabled={!newRole.label.trim() || !nameValid || createMut.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="role-new-submit"
              >
                {createMut.isPending ? tx('Saving...') : tx('Create role')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMut.mutate()}
        title={`${tx('Delete role')} "${selected?.label ?? ''}"`}
        description={tx('The role will be removed. Admins must be moved to another role first.')}
        confirmLabel={tx('Delete role')}
        variant="danger"
        isLoading={deleteMut.isPending}
      />
    </div>
  )
}

export default PermissionsPage
